/**
 * Μήνυμα κουζίνας από το ταμείο (ρύθμιση kitchen_messages_enabled): τα έτοιμα μηνύματα του
 * καταστήματος («ΠΑΣΟ ΚΥΡΙΩΣ», «ΟΛΑ ΜΑΖΙ» …) και ελεύθερο κείμενο.
 *
 * Δύο χρήσεις: «send» — φεύγει αμέσως μόνο του (ενέργειες τραπεζιού)· «attach» — φεύγει μαζί
 * με τον γύρο που ετοιμάζεται (τυπώνεται στην κορυφή του δελτίου κουζίνας). Δεν είναι είδος:
 * ούτε τιμή, ούτε καλάθι, ούτε παραστατικό.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, Loader2, Plus } from 'lucide-react';
import {
  KITCHEN_MESSAGE_MAX_LENGTH,
  KITCHEN_MESSAGES_PER_SEND,
  isKitchenMessageSelected,
  toggleKitchenMessage,
  withDraftMessage,
} from '../../utils/kitchenMessages';

interface KitchenMessageModalProps {
  tableNumber?: string | number;
  presets: string[];
  mode: 'send' | 'attach';
  /** Τα ήδη επιλεγμένα (μόνο στο «attach»). */
  initial?: string[];
  busy?: boolean;
  isMobile?: boolean;
  onConfirm: (messages: string[]) => void;
  onClose: () => void;
}

const KitchenMessageModal: React.FC<KitchenMessageModalProps> = ({
  tableNumber,
  presets,
  mode,
  initial = [],
  busy = false,
  isMobile = false,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>(initial);
  const [draft, setDraft] = useState('');

  // Ελεύθερα κείμενα της επιλογής (όσα δεν είναι στη λίστα του καταστήματος).
  const custom = selected.filter((m) => !presets.some((p) => isKitchenMessageSelected([p], m)));
  const full = selected.length >= KITCHEN_MESSAGES_PER_SEND;
  const finalMessages = withDraftMessage(selected, draft);
  const canConfirm = !busy && (mode === 'attach' || finalMessages.length > 0);

  const addDraft = () => {
    setSelected((prev) => withDraftMessage(prev, draft));
    setDraft('');
  };

  return (
    <div className="cashier-overlay fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className="cashier-dialog bg-surface-1 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-full">
        <div className="cashier-dialog-header px-6 py-4 bg-surface-2 border-b border-line-strong flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xl font-bold text-t1">{t('dineIn:kitchenMessage', { defaultValue: 'Μήνυμα κουζίνας' })}</h2>
            <p className="text-sm text-t3">
              {mode === 'attach'
                ? t('dineIn:kitchenMessageWithOrder', { defaultValue: 'Μαζί με την παραγγελία · Τραπέζι {{number}}', number: tableNumber })
                : t('dineIn:tableNumber', { number: tableNumber })}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
            className={`hover:bg-surface-3 rounded-xl transition-colors ${isMobile ? 'p-3' : 'p-2'}`}
          >
            <X className="w-6 h-6 text-t3" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto min-h-0">
          {presets.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {presets.map((message) => {
                const on = isKitchenMessageSelected(selected, message);
                return (
                  <button
                    key={message}
                    type="button"
                    aria-pressed={on}
                    disabled={!on && full}
                    onClick={() => setSelected((prev) => toggleKitchenMessage(prev, message))}
                    className={`cashier-choice min-h-[56px] px-3 py-2 rounded-xl font-bold text-sm leading-tight transition-colors disabled:opacity-40 ${
                      on ? 'bg-teal-600 text-white' : 'bg-surface-3 text-t1 hover:bg-surface-4'
                    }`}
                  >
                    {message}
                  </button>
                );
              })}
            </div>
          )}

          {custom.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {custom.map((message) => (
                <span key={message} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-lg bg-teal-600 text-white text-sm font-bold">
                  {message}
                  <button
                    type="button"
                    onClick={() => setSelected((prev) => toggleKitchenMessage(prev, message))}
                    aria-label={t('common:remove', { defaultValue: 'Αφαίρεση' })}
                    className="p-1 rounded-md hover:bg-teal-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div>
            <label className="text-sm font-semibold text-t3 block mb-2">
              {t('dineIn:kitchenMessageOther', { defaultValue: 'Άλλο μήνυμα' })}
            </label>
            <div className="flex gap-2">
              <input
                value={draft}
                maxLength={KITCHEN_MESSAGE_MAX_LENGTH}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDraft(); } }}
                disabled={full}
                placeholder={t('dineIn:kitchenMessagePlaceholder', { defaultValue: 'π.χ. ΚΑΘΥΣΤΕΡΗΣΗ 10 ΛΕΠΤΑ' })}
                className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-surface-2 border border-line-strong text-t1 placeholder-t4 focus:outline-none focus:border-teal-500 disabled:opacity-50"
              />
              <button
                type="button"
                onClick={addDraft}
                disabled={full || !draft.trim()}
                className="px-4 rounded-xl bg-surface-3 text-t1 font-semibold hover:bg-surface-4 disabled:opacity-40 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                {t('dineIn:kitchenMessageAdd', { defaultValue: 'Προσθήκη' })}
              </button>
            </div>
          </div>

          <p className="text-xs text-t4">
            {t('dineIn:kitchenMessageHint', {
              defaultValue: 'Έως {{max}} μηνύματα. Τυπώνονται με μεγάλα γράμματα στους εκτυπωτές της κουζίνας — δεν είναι είδος και δεν μπαίνουν σε παραστατικό.',
              max: KITCHEN_MESSAGES_PER_SEND,
            })}
          </p>
        </div>

        <div className="px-6 py-4 border-t border-line-strong bg-surface-2 shrink-0">
          <button
            onClick={() => onConfirm(finalMessages)}
            disabled={!canConfirm}
            className="cashier-primary w-full py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all active:scale-95"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
            {mode === 'attach'
              ? t('dineIn:kitchenMessageAttach', { defaultValue: 'Μαζί με την παραγγελία' })
              : t('dineIn:kitchenMessageSend', { defaultValue: 'Αποστολή στην κουζίνα' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KitchenMessageModal;
